import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/Sidebar.css';
import { filterMenuByPermissions, getCurrentUserPermissions } from '../utils/permissions';

// Menú completo (constante fuera del componente)
const ALL_MENU_ITEMS = [
  { path: '/dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard', description: 'Panel principal' },
  { path: '/professions', icon: 'fas fa-user-md', label: 'Profesiones', description: 'Catálogo de profesiones' },
  { path: '/especialidades-medicas', icon: 'fas fa-notes-medical', label: 'Especialidades', description: 'Especialidades médicas' },
  { path: '/tipo-socia', icon: 'fas fa-id-badge', label: 'Tipo Socia', description: 'Clasificación de socias' },
  { path: '/tallas', icon: 'fas fa-ruler', label: 'Tallas', description: 'Gestión de tallas' },
  { path: '/partners', icon: 'fas fa-user-friends', label: 'Socias', description: 'Gestión de socias' },
  { path: '/syndrome-characteristics', icon: 'fas fa-dna', label: 'Características', description: 'Síndrome de Turner' },
  { path: '/inventory', icon: 'fas fa-boxes', label: 'Inventario', description: 'Material y recursos' },
  { path: '/categoria-inventario', icon: 'fas fa-tags', label: 'Categorías Inv.', description: 'Categorías de inventario' },
  { path: '/ubicacion-inventario', icon: 'fas fa-map-marker-alt', label: 'Ubicaciones Inv.', description: 'Ubicaciones de inventario' },
  { path: '/mentors', icon: 'fas fa-chalkboard-teacher', label: 'Mentores', description: 'Gestión de mentores' },
  { path: '/medicos', icon: 'fas fa-stethoscope', label: 'Médicos', description: 'Gestión de médicos' },
  { path: '/hospitals', icon: 'fas fa-hospital', label: 'Hospitales', description: 'Instituciones' },
  { path: '/tratamientos', icon: 'fas fa-pills', label: 'Tratamientos', description: 'Tratamientos médicos' },
  { path: '/requisitos-socia', icon: 'fas fa-file-alt', label: 'Requisitos', description: 'Documentación socias' },
  { path: '/users', icon: 'fas fa-users-cog', label: 'Usuarios', description: 'Gestión de usuarios' },
  { path: '/reports', icon: 'fas fa-chart-bar', label: 'Reportes', description: 'Indicadores y listados' },
  { path: '/editar-formatos', icon: 'fas fa-file-signature', label: 'Editar formatos', description: 'Administrar formatos (carnet / solicitud)' },
  { path: '/change-password', icon: 'fas fa-key', label: 'Contraseña', description: 'Seguridad de cuenta' }
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSidebar = () => setIsOpen(prev => !prev);
  const handleMenuClick = (path) => { navigate(path); setIsOpen(false); };
  // Filtrar menú según permisos del usuario usando useMemo
  const filteredMenu = useMemo(() => {
    const userPermissions = getCurrentUserPermissions();
    return filterMenuByPermissions(ALL_MENU_ITEMS, userPermissions);
  }, []); // Se calcula al montar; actualizar si la app cambia la forma de guardar permisos

  if (location.pathname === '/') return null;

  return (
    <>
      <button
        className={`sidebar-toggle ${isOpen ? 'active' : ''}`}
        onClick={toggleSidebar}
        aria-label="Toggle menu"
        style={{
          position: 'fixed', top: '70px', left: '20px', zIndex: 1002, background: 'white',
          border: '2px solid #ccc', borderRadius: '8px', padding: '12px', cursor: 'pointer',
          opacity: isOpen ? 0 : 1, pointerEvents: isOpen ? 'none' : 'all', transform: isOpen ? 'scale(0.8)' : 'scale(1)',
          transition: 'all 0.3s ease'
        }}
      >
        <span style={{ display: 'block', width: '20px', height: '2px', backgroundColor: '#333', marginBottom: '4px' }} />
        <span style={{ display: 'block', width: '20px', height: '2px', backgroundColor: '#333', marginBottom: '4px' }} />
        <span style={{ display: 'block', width: '20px', height: '2px', backgroundColor: '#333' }} />
      </button>

      {isOpen && <div className="sidebar-overlay" onClick={() => setIsOpen(false)} />}

      <nav
        className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}
        style={{
          position: 'fixed', top: 0, left: isOpen ? 0 : '-320px', width: '320px', height: '100vh',
          backgroundColor: 'white', borderRight: '1px solid #ccc', boxShadow: '4px 0 20px rgba(0,0,0,0.1)',
          transition: 'left 0.3s ease', zIndex: 1001
        }}
      >
        <div className="sidebar-header" style={{ padding: '20px', borderBottom: '1px solid #ccc', backgroundColor: '#821C53' }}>
          <div className="sidebar-brand" style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'white' }}>
            <i className="fas fa-heartbeat" style={{ fontSize: '24px' }} />
            <span className="brand-text" style={{ fontSize: '24px', fontWeight: 'bold' }}>FEPAST</span>
          </div>
        </div>
        <div className="sidebar-content" style={{ flex: 1, overflowY: 'auto', padding: '20px 0' }}>
          <ul className="sidebar-menu" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredMenu.map(item => (
              <li key={item.path} className="sidebar-item" style={{ marginBottom: '8px' }}>
                <button
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item.path)}
                  style={{
                    display: 'flex', alignItems: 'center', width: '100%', padding: '12px 20px',
                    background: location.pathname === item.path ? 'rgba(130,28,83,0.1)' : 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', color: location.pathname === item.path ? '#821C53' : '#333'
                  }}
                >
                  <div className="sidebar-icon" style={{ marginRight: '12px' }}>
                    <i className={item.icon} style={{ fontSize: '18px' }} />
                  </div>
                  <div className="sidebar-text" style={{ flex: 1 }}>
                    <span className="sidebar-label" style={{ display: 'block', fontSize: '16px', fontWeight: '500' }}>{item.label}</span>
                    <span className="sidebar-description" style={{ display: 'block', fontSize: '14px', color: '#666' }}>{item.description}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;